'use client';
import { navItems } from '@/config/nav-config';
import {
  KBarAnimator,
  KBarPortal,
  KBarPositioner,
  KBarProvider,
  KBarSearch
} from 'kbar';
import { useRouter } from '@/i18n/routing';
import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import RenderResults from './render-result';
import useThemeSwitching from './use-theme-switching';
import { useFilteredNavItems } from '@/hooks/use-nav';
import type { NavItem } from '@/types';

export default function KBar({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const filteredNavItems = useFilteredNavItems(navItems);
  const t = useTranslations();
  const tKbar = useTranslations('navigation.kbar');

  const resolveTitle = (item: NavItem): string => {
    if (item.titleKey) {
      try {
        return t(item.titleKey as never);
      } catch {
        return item.title;
      }
    }
    return item.title;
  };

  // These action are for the navigation
  const actions = useMemo(() => {
    // Define navigateTo inside the useMemo callback to avoid dependency array issues
    const navigateTo = (url: string) => {
      router.push(url);
    };

    return filteredNavItems.flatMap((navItem) => {
      const navItemTitle = resolveTitle(navItem);

      // Only include base action if the navItem has a real URL and is not just a container
      const baseAction =
        navItem.url !== '#'
          ? {
              id: `${navItem.title.toLowerCase()}Action`,
              name: navItemTitle,
              shortcut: navItem.shortcut,
              keywords: `${navItemTitle.toLowerCase()} ${navItem.title.toLowerCase()}`,
              section: tKbar('navigation'),
              subtitle: tKbar('goTo', { title: navItemTitle }),
              perform: () => navigateTo(navItem.url)
            }
          : null;

      // Map child items into actions
      const childActions =
        navItem.items?.map((childItem) => {
          const childTitle = resolveTitle(childItem);
          return {
            id: `${childItem.title.toLowerCase()}Action`,
            name: childTitle,
            shortcut: childItem.shortcut,
            keywords: `${childTitle.toLowerCase()} ${childItem.title.toLowerCase()}`,
            section: navItemTitle,
            subtitle: tKbar('goTo', { title: childTitle }),
            perform: () => navigateTo(childItem.url)
          };
        }) ?? [];

      // Return only valid actions (ignoring null base actions for containers)
      return baseAction ? [baseAction, ...childActions] : childActions;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [router, filteredNavItems, t, tKbar]);

  return (
    // toggleShortcut moved off the default `$mod+k` because Cmd+K is now
    // owned by the OMA-548 system-wide search dialog (the conventional
    // binding for "search" across modern apps). kbar nav palette keeps
    // its keyboard entry via Cmd+Shift+K.
    <KBarProvider
      actions={actions}
      options={{ toggleShortcut: '$mod+Shift+k' }}
    >
      <KBarComponent>{children}</KBarComponent>
    </KBarProvider>
  );
}
const KBarComponent = ({ children }: { children: React.ReactNode }) => {
  useThemeSwitching();

  return (
    <>
      <KBarPortal>
        <KBarPositioner className='bg-background/80 fixed inset-0 z-99999 p-0! backdrop-blur-sm'>
          <KBarAnimator className='bg-card text-card-foreground relative mt-64! w-full max-w-[600px] -translate-y-12! overflow-hidden rounded-lg border shadow-lg'>
            <div className='bg-card border-border sticky top-0 z-10 border-b'>
              <KBarSearch className='bg-card w-full border-none px-6 py-4 text-lg outline-hidden focus:ring-0 focus:ring-offset-0 focus:outline-hidden' />
            </div>
            <div className='max-h-[400px]'>
              <RenderResults />
            </div>
          </KBarAnimator>
        </KBarPositioner>
      </KBarPortal>
      {children}
    </>
  );
};
